import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Chip from "@mui/material/Chip";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMemo, useRef } from "react";
import {
  stringToDateTimeString,
  stringToDateString,
} from "../../../utils/functions";

/**
 * Const
 */
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export const FilterInfonavitRequests = ({
  handleFilters,
  cleanFilters,
  setNoContent,
  setFilterPayload,
  nrpList,
  hideEmployeeName,
}) => {
  /**
   * t of useTranslation to translate
   */
  const { t } = useTranslation();

  const selectAllItem = {
    label: t("App.infonavitRequests.filter.selectAll"),
    value: t("App.infonavitRequests.filter.selectAll"),
  };

  const nrpListWithToggle = useMemo(() => {
    return nrpList.length > 0 ? [selectAllItem, ...nrpList] : [];
  }, [nrpList]);

  const enabledField = useRef("");

  /**
   * Schema
   */
  const schema = yup.object().shape({
    nrps: yup.array().min(
      1,
      t("App.validationMessages.requiredSelectV2", {
        complement: t("App.infonavitRequests.filter.nrpsRequired"),
      })
    ),

    fechaIni: yup.string().required(
      t("App.validationMessages.requiredSelectV2", {
        complement: t("App.infonavitRequests.filter.dateIniRequired"),
      })
    ),
    fechaFin: yup.string().required(
      t("App.validationMessages.requiredSelectV2", {
        complement: t("App.infonavitRequests.filter.dateEndRequired"),
      })
    ),
  });

  /**
   * useForm to resolver the schema validation
   */
  const {
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nrps: [],
      fechaIni: "",
      fechaFin: "",
      employeeName: "",
      nss: "",
      numCredit: "",
    },
  });

  /**
   * filterOptions para el autocomplete de compañías
   */
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 200,
  });

  /**
   *
   * Función que se acciona cuando se cambia el valor del autocomplete de NRPs para cambiar el valor del state de nrpsSelected que es lo que se muestra seleccionado en el autcomplete
   * También funciona para seleccionar todos los valores cuando se selecciona la opción de 'Seleccionar todos'
   *
   * @param {Event} event
   * @param {Values} values comercios
   * @param {String} reason tipo de selección; selectOption, removeOption
   * @param {Object} detail opción elegida
   */
  const onFileChange = (event, values, reason, detail) => {
    const option = detail?.option ?? null;
    values = values.filter((item) => item.value !== selectAllItem.value);

    if (reason === "removeOption" && option?.value === selectAllItem.value) {
      values = [];
    } else if (
      reason === "selectOption" &&
      option?.value === selectAllItem.value
    ) {
      values = nrpList;
    }

    return values.length === nrpList.length
      ? [...values, selectAllItem]
      : values;
  };

  /**
   * Function to clear the fields of the filter form
   */
  const handleCleanFilters = () => {
    setValue("nrps", []);
    setValue("fechaIni", "");
    setValue("fechaFin", "");
    setValue("employeeName", "");
    setValue("nss", "");
    setValue("numCredit", "");

    setNoContent(false);
    cleanFilters();
    setFilterPayload(null);
  };

  /**
   * Function to prepare payload and send it to handleFilters
   */
  const preparePayload = (payload) => {
    const endFilterDate = stringToDateString(payload.fechaFin) + " 23:59:59";

    const tempNrpsList = payload.nrps.filter(
      (item) => item.label !== selectAllItem.label
    );

    const newPayload = {
      nrps: tempNrpsList.map((nrp) => nrp.value),
      initFilterDate: stringToDateTimeString(payload.fechaIni),
      endFilterDate,
    };

    if (payload?.employeeName) newPayload.nombreTrab = payload.employeeName;
    if (payload?.nss) newPayload.nss = payload.nss;
    if (payload?.numCredit) newPayload.numCredito = payload.numCredit;

    setFilterPayload(newPayload);
    return handleFilters(newPayload);
  };

  return (
    <Accordion defaultExpanded={true} expanded={true} disableGutters>
      <Grid item className="">
        <AccordionSummary aria-controls="panel2a-content" id="panel2a-header">
          <TuneOutlinedIcon
            sx={{
              fontSize: 20,
              color: "#543ab4",
            }}
          />
          <p className="navbar__text" style={{ paddingLeft: "1rem" }}>
            {t("App.catalogue.filterName")}
          </p>
        </AccordionSummary>
      </Grid>

      <AccordionDetails>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs="12" md className="fixed-side-line">
            <form id="hook-form" onSubmit={handleSubmit(preparePayload)}>
              <Grid container spacing={{ xs: 0, lg: 3 }}>
                {/* -------- NRPs -------- */}
                <Grid item xs="12" lg="4">
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs="12">
                      <label className="form__label label__text-grey">
                        {t("App.infonavitRequests.filter.nrps")}
                      </label>
                    </Grid>

                    <Grid item xs="12">
                      <Controller
                        name="nrps"
                        render={({ field: { onChange, value } }) => (
                          <Autocomplete
                            noOptionsText={t(
                              "App.validationMessages.noMatchFilter"
                            )}
                            className="select__filters__full"
                            multiple
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            filterOptions={filterOptions}
                            value={value || []}
                            options={nrpListWithToggle}
                            disableCloseOnSelect
                            limitTags={1}
                            onChange={(event, values, reason, detail) => {
                              onChange(
                                onFileChange(event, values, reason, detail)
                              );
                            }}
                            getOptionLabel={(option) => option.label}
                            renderOption={(props, option, { selected }) => (
                              <li key={option.label} {...props}>
                                <Checkbox
                                  icon={icon}
                                  className="autoComplete__checkbox"
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option.label}
                              </li>
                            )}
                            renderTags={(values) => {
                              const size = values.filter(
                                (item) => item.value !== selectAllItem.value
                              ).length;
                              const label =
                                size > 1
                                  ? t("App.autocomplete.selected")
                                  : t("App.autocomplete.oneSelected");

                              return (
                                <Chip
                                  label={`${size} ${label}`}
                                  className="autocomplete-chip"
                                  variant="filled"
                                />
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t(
                                  "App.infonavitRequests.filter.nrpsPlaceholder"
                                )}
                              />
                            )}
                          />
                        )}
                        control={control}
                      />
                    </Grid>

                    <Grid item xs="12">
                      <label className="form__label label__error-color">
                        {errors.nrps ? errors.nrps.message : ""}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- FechaIni -------  */}
                <Grid item xs="12" lg="4">
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs="12">
                      <label className="form__label label__text-grey">
                        {t("App.infonavitRequests.filter.dateIni")}
                      </label>
                    </Grid>

                    <Grid item xs="12">
                      <Controller
                        defaultValue=""
                        control={control}
                        name="fechaIni"
                        render={({ field: { onChange, value } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              className="select__filters__full select--fixDate"
                              inputFormat="YYYY-MM-DD"
                              minDate="2005-01-07"
                              maxDate={new Date()}
                              sx={{
                                translate: "0px -5px",
                              }}
                              value={
                                watch("fechaIni") ? watch("fechaIni") : null
                              }
                              onChange={onChange}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  onKeyDown={(event) => event.preventDefault()}
                                  onKeyUp={(event) => event.preventDefault()}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>

                    <Grid item xs="12">
                      <label className="form__label label__error-color">
                        {errors.fechaIni ? errors.fechaIni.message : ""}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- FechaFin -------  */}
                <Grid item xs="12" lg="4">
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs="12">
                      <label className="form__label label__text-grey">
                        {t("App.infonavitRequests.filter.dateEnd")}
                      </label>
                    </Grid>

                    <Grid item xs="12">
                      <Controller
                        defaultValue=""
                        control={control}
                        name="fechaFin"
                        render={({ field: { onChange, value } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              className="select__filters__full select--fixDate"
                              inputFormat="YYYY-MM-DD"
                              minDate="2005-01-07"
                              maxDate={new Date()}
                              sx={{
                                translate: "0px -5px",
                              }}
                              value={
                                watch("fechaFin") ? watch("fechaFin") : null
                              }
                              onChange={onChange}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  onKeyDown={(event) => event.preventDefault()}
                                  onKeyUp={(event) => event.preventDefault()}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>

                    <Grid item xs="12">
                      <label className="form__label label__error-color">
                        {errors.fechaFin ? errors.fechaFin.message : ""}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- EmployeeName -------  */}
                {!hideEmployeeName ? (
                  <Grid item xs="12" lg="4">
                    <Grid
                      container
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      <Grid item xs="12">
                        <label
                          className={
                            (watch("employeeName")
                              ? "label__text-grey"
                              : "label__text-disable") + " form__label"
                          }
                        >
                          {t("App.infonavitRequests.filter.employeeName")}
                        </label>
                      </Grid>

                      <Grid item xs="12">
                        <Controller
                          name="employeeName"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <TextField
                              onChange={(...params) => {
                                enabledField.current =
                                  params[0].target.value === ""
                                    ? ""
                                    : "employeeName";
                                onChange(...params);
                              }}
                              placeholder={t(
                                "App.infonavitRequests.filter.employeeName"
                              )}
                              disabled={
                                !["", "employeeName"].includes(
                                  enabledField.current
                                )
                              }
                              value={value}
                              type="text"
                              className={
                                "select__filters__full" +
                                (!value ? " select__filters--disabled" : "")
                              }
                              FormHelperTextProps={{
                                style: {
                                  margin: "-5px 0 1px 0px",
                                  color: "#e52900",
                                  fontSize: 10,
                                },
                              }}
                              InputLabelProps={{
                                style: {
                                  margin: "-5px 0 0 -10px",
                                  padding: "0 0 0 5px",
                                  color: "#8c8d9d",
                                  fontSize: 13,
                                },
                              }}
                              inputProps={{
                                maxLength: "50",
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs="12">
                        <label className="form__label label__error-color">
                          {errors.employeeName
                            ? errors.employeeName.message
                            : ""}
                        </label>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  ""
                )}

                {/* -------- NSS -------  */}
                <Grid item xs="12" lg="4">
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs="12">
                      <label
                        className={
                          (watch("nss")
                            ? "label__text-grey"
                            : "label__text-disable") + " form__label"
                        }
                      >
                        {t("App.infonavitRequests.filter.nss")}
                      </label>
                    </Grid>

                    <Grid item xs="12">
                      <Controller
                        name="nss"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            onChange={(...params) => {
                              enabledField.current =
                                params[0].target.value === "" ? "" : "nss";
                              onChange(...params);
                            }}
                            placeholder={t("App.infonavitRequests.filter.nss")}
                            disabled={
                              !["", "nss"].includes(enabledField.current)
                            }
                            value={value}
                            type="text"
                            className={
                              "select__filters__full" +
                              (!value ? " select__filters--disabled" : "")
                            }
                            FormHelperTextProps={{
                              style: {
                                margin: "-5px 0 1px 0px",
                                color: "#e52900",
                                fontSize: 10,
                              },
                            }}
                            InputLabelProps={{
                              style: {
                                margin: "-5px 0 0 -10px",
                                padding: "0 0 0 5px",
                                color: "#8c8d9d",
                                fontSize: 13,
                              },
                            }}
                            inputProps={{
                              maxLength: "11",
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs="12">
                      <label className="form__label label__error-color">
                        {errors.nss ? errors.nss.message : ""}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>

                {/* -------- numCredit -------  */}
                <Grid item xs="12" lg="4">
                  <Grid
                    container
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs="12">
                      <label
                        className={
                          (watch("numCredit")
                            ? "label__text-grey"
                            : "label__text-disable") + " form__label"
                        }
                      >
                        {t("App.infonavitRequests.filter.numCredit")}
                      </label>
                    </Grid>

                    <Grid item xs="12">
                      <Controller
                        name="numCredit"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            onChange={(...params) => {
                              enabledField.current =
                                params[0].target.value === ""
                                  ? ""
                                  : "numCredit";
                              onChange(...params);
                            }}
                            placeholder={t(
                              "App.infonavitRequests.filter.numCredit"
                            )}
                            disabled={
                              !["", "numCredit"].includes(enabledField.current)
                            }
                            value={value}
                            type="text"
                            className={
                              "select__filters__full" +
                              (!value ? " select__filters--disabled" : "")
                            }
                            FormHelperTextProps={{
                              style: {
                                margin: "-5px 0 1px 0px",
                                color: "#e52900",
                                fontSize: 10,
                              },
                            }}
                            InputLabelProps={{
                              style: {
                                margin: "-5px 0 0 -10px",
                                padding: "0 0 0 5px",
                                color: "#8c8d9d",
                                fontSize: 13,
                              },
                            }}
                            inputProps={{
                              maxLength: "11",
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs="12">
                      <label className="form__label label__error-color">
                        {errors.numCredit ? errors.numCredit.message : ""}
                      </label>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>

          <Grid md="auto" item>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              gap={2}
              width={{ xs: "15rem", md: "100%" }}
              flex-direction={{ xs: "column", md: "row" }}
            >
              <Grid item>
                <Button
                  startIcon={
                    <TuneOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: "white",
                      }}
                    />
                  }
                  disableRipple
                  // disabled={watch("product") || watch("role") ? false : true}
                  className={
                    "btn__applyFilter"
                    // watch("product") || watch("role")
                    //   ? "btn__applyFilter"
                    //   : "btn__applyFilter-disabled"
                  }
                  type="submit"
                  form="hook-form"
                >
                  {t("App.listUsers.applyFilters")}
                </Button>
              </Grid>

              {/* <Grid item xs="12" lg="auto"></Grid> */}

              <Grid item>
                <Button
                  disableRipple
                  className="btn__deleteFilter"
                  onClick={handleCleanFilters}
                >
                  {t("App.listUsers.cleanFilters")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
